import React, {
  createContext,
  useState,
  useCallback,
  ReactNode,
  useContext,
} from "react";
import type {
  DateTimeValue,
  IntradayChartData,
  TodayInMarketsCategory,
  TodayInMarketsItem,
} from "~types/types";

export interface TodayInMarketsContextValue {
  categories: TodayInMarketsCategory[];
  selectedCategory: TodayInMarketsCategory;
  onCategoryChange: (categoryId: string) => void;
  activeTicker?: string;
  updateActiveTicker: (ticker?: string) => void;
  updateCategories: (categories: TodayInMarketsCategory[]) => void;
  updateCategoryWithSparklineData: (
    categoryId: string,
    tickers: IntradayChartData[]
  ) => void;
}

const TodayInMarketsContext = createContext<
  TodayInMarketsContextValue | undefined
>(undefined);

TodayInMarketsContext.displayName = "TodayInMarketsContext";

interface TodayInMarketsContextProviderProps {
  categories: TodayInMarketsCategory[];
  children: ReactNode;
}

export const TodayInMarketsContextProvider: React.FC<
  TodayInMarketsContextProviderProps
> = ({ categories: initialCategories, children }) => {
  const [categories, setCategories] =
    useState<TodayInMarketsCategory[]>(initialCategories);
  const [selectedCategory, setSelectedCategory] =
    useState<TodayInMarketsCategory>(initialCategories[0]);
  const [activeTicker, setActiveTicker] = useState<string | undefined>(
    undefined
  );

  const onCategoryChange = useCallback(
    (categoryId: string) => {
      // Find the matching category (fallback to first)
      const found = categories.find((category) => category.id === categoryId);
      const newCategory = found || categories[0];

      setSelectedCategory(newCategory);
    },
    [categories]
  );

  const updateActiveTicker = useCallback((ticker?: string) => {
    setActiveTicker(ticker);
  }, []);

  const updateCategories = useCallback(
    (updatedCategories: TodayInMarketsCategory[]) => {
      // merge updated category ticker data with existing category data
      setCategories((prevCategories) => {
        return updatedCategories.map((updatedCategory) => {
          // check if the new category exists
          const existingCategory = prevCategories.find(
            (category) => category.id === updatedCategory.id
          );

          // new category doesn't exist, return updated data
          if (!existingCategory) {
            return { ...updatedCategory };
          }

          // category exists in both new and old list,
          const todayInMarketsItemMap = new Map<string, TodayInMarketsItem>();

          existingCategory.dataItems.forEach((item) =>
            todayInMarketsItemMap.set(item.id, item)
          );

          // merge the markets items ticker data, retain the priceMovements data for sparkline.
          // Sparkline data will be updated via `updateCategorySparklineData`, when data is refreshed
          existingCategory.dataItems = updatedCategory.dataItems.map((item) => {
            const existingItem = todayInMarketsItemMap.get(item.id);

            if (!existingItem) {
              return { ...item };
            }

            return {
              ...item,
              priceMovements: existingItem.priceMovements || [],
              priceSet: existingItem.priceSet || [],
            };
          });

          return { ...existingCategory };
        });
      });
    },
    []
  );

  const updateCategoryWithSparklineData = useCallback(
    (categoryId: string, chartData: IntradayChartData[]) => {
      // skip updating, when chart data is not available
      if (!chartData?.length) {
        return;
      }

      const ticketChartDataMap = new Map<string, DateTimeValue[]>();

      chartData.forEach((tickerChartData) =>
        ticketChartDataMap.set(tickerChartData.id, tickerChartData.series)
      );

      setCategories((prevCategories) => {
        const updatedCategory = prevCategories.find(
          (category) => category.id === categoryId
        );

        // category not found, skip updating
        if (!updatedCategory) {
          return prevCategories;
        }

        // update category items in-place
        updatedCategory.dataItems = updatedCategory.dataItems.map((item) => {
          const priceMovements = ticketChartDataMap.get(item.id);
          const priceSet = priceMovements?.map((point) => point?.value);

          return {
            ...item,
            priceMovements: priceMovements || [],
            priceSet: priceSet || [],
          };
        });

        return [...prevCategories];
      });
    },
    []
  );

  return (
    <TodayInMarketsContext.Provider
      value={{
        categories,
        selectedCategory,
        activeTicker,
        onCategoryChange,
        updateActiveTicker,
        updateCategories,
        updateCategoryWithSparklineData,
      }}
    >
      {children}
    </TodayInMarketsContext.Provider>
  );
};

/**
 * Hook to consume events and selected-tab state
 * @throws if used outside of TodayInMarketsContextProvider
 */
export function useTodayInMarkets(): TodayInMarketsContextValue {
  const context = useContext(TodayInMarketsContext);

  if (!context) {
    throw new Error(
      "useTodayInMarkets must be used within TodayInMarketsContextProvider"
    );
  }

  return context;
}
